// Todo: Check and remove file if not in use.
import { atom } from 'jotai';
import { FundGetData } from '~/api/def/endpoints/fundEndpoint';
import {
  endpointFundValuationGET,
  FundValuation,
  FundValuationGuard,
} from '~/api/def/endpoints/fundValuationEndpoint';
import { CrudErrorResp } from '~/api/def/endpoints/types';
import { toastAtom } from '~/atoms/toastAtom';
import { FundDashboardAnalytics } from '~/components/ui/tables/ag/fundListDashboard/FundListDashboard';
import { ChartSeries } from '~/lib/chart/chartTypes';
import { yesterdayInApiStr } from '~/lib/date/yesterdayInApiStr';
import { AddNull, Hierarchy } from '~/types';

export const fundHierarchyAtom = atom<Hierarchy[] | null>(null);
export const existingFundSetAtom = atom<Set<string>>((get) => {
  const existingFunds = new Set<string>();
  const hierarchy = get(fundHierarchyAtom);
  if (hierarchy) {
    hierarchy.forEach((jurisdiction) => {
      jurisdiction.funds.forEach((fund) => {
        existingFunds.add(fund.fundId);
      });
    });
  }
  return existingFunds;
});

export const fundListAtom = atom<FundGetData[]>([]);
export const fundOverviewDashboardAtom = atom<FundDashboardAnalytics[]>([]);
export const fundValuationYesterdayAtom = atom<FundValuation[] | null>(null);

export const fundMetricsAtom = atom<FundMetrics>({
  totalAum: 0,
  totalPortfolioCompanies: 0,
  activeFunds: 0,
});
export const netIrrSeriesAtom = atom<ChartSeries>([]);
export const grossIrrSeriesAtom = atom<ChartSeries>([]);
export const netTvpiSeriesAtom = atom<ChartSeries>([]);
export const grossMoicSeriesAtom = atom<ChartSeries>([]);

export const createTimeSeriesAtoms = atom(
  null,
  (get, set, valuations: FundValuation[]) => {
    // const netIrrSeries = seriesDateSeriesFromFunds(valuations,'netIrr')
    set(netIrrSeriesAtom, seriesDateSeriesFromFunds(valuations, 'netIrr'));
    set(grossIrrSeriesAtom, seriesDateSeriesFromFunds(valuations, 'grossIrr'));
  },
);
export const createDashboardTableRowsAtom = atom(
  null,
  async (get, set, fundList: FundGetData[]) => {
    let yesterdayValuations:
      | FundValuation[]
      | CrudErrorResp
      | undefined
      | null = get(fundValuationYesterdayAtom);
    if (!yesterdayValuations) {
      const yesterdayValuationsResponse = await endpointFundValuationGET({
        valuationDate: yesterdayInApiStr(),
      });
      if (FundValuationGuard(yesterdayValuationsResponse?.data)) {
        yesterdayValuations = yesterdayValuationsResponse.data;
      } else {
        yesterdayValuations = [];
        const toastRef = get(toastAtom);
        if (toastRef) {
          toastRef.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to fetch fund valuations',
          });
        }
      }
      set(fundValuationYesterdayAtom, yesterdayValuations?.data);
      const rows = mergeFundsAndValuations(fundList, yesterdayValuations?.data);
      set(fundOverviewDashboardAtom, rows);
      set(fundMetricsAtom, fundsMetricsCompute(rows));
      const netIrrSeries = rows.map((fund) => ({
        name: fund.name,
        label: fund.name,
        value: fund.netIrr,
      }));
      set(netIrrSeriesAtom, netIrrSeries);
      set(grossMoicSeriesAtom, seriesGetFromFunds(rows, 'grossMoic'));
      set(netTvpiSeriesAtom, seriesGetFromFunds(rows, 'netTvpi'));
    }
  },
);

export const fundIdAtom = atom<string | null>(null);

export type FundMetrics = {
  totalAum: number;
  totalPortfolioCompanies: number;
  activeFunds: number;
};
function fundsMetricsCompute(funds: FundDashboardAnalytics[]): FundMetrics {
  const totalAum = funds.reduce((acc, fund) => acc + fund.aum, 0);
  const totalPortfolioCompanies = funds.reduce(
    (acc, fund) => acc + fund.portfolioCompanyCount,
    0,
  );
  const activeFunds = funds.filter((fund) => fund.status !== 'Closed').length;
  return { totalAum, totalPortfolioCompanies, activeFunds };
}

function seriesDateSeriesFromFunds(
  funds: FundValuation[],
  key: keyof FundValuation,
): ChartSeries {
  return funds.map((fund) => {
    const date = new Date(fund.valuationDate).toISOString().substring(0, 10);
    return {
      name: date,
      label: date,
      value: fund[key] as number,
    };
  });
}
function seriesGetFromFunds(
  funds: FundDashboardAnalytics[],
  key: keyof FundDashboardAnalytics,
): ChartSeries {
  return funds.map((fund) => ({
    name: fund.name,
    label: fund.name,
    value: fund[key] as number,
  }));
}
function mergeFundsAndValuations(
  funds: FundGetData[],
  valuations: FundValuation[],
): FundDashboardAnalytics[] {
  return funds.map((fund) => {
    const valuationResponse = getFundValuation(fund.id, valuations);
    const valuation = valuationResponse
      ? { ...emptyValuation, ...valuationResponse }
      : emptyValuation;
    return {
      aum: typeof fund.size === 'number' ? fund.size : 0,
      currency: typeof fund.currency === 'string' ? fund.currency : '',
      fundId: fund.id,
      grossIrr: getNumericValue(valuation.grossIrr),
      netIrr: getNumericValue(valuation.netIrr),
      grossMoic: getNumericValue(valuation.grossMoic),
      netTvpi: getNumericValue(valuation.netTvpi),
      currentNav: getNumericValue(valuation.currentNav),
      investmentFocus: valuation.investmentFocus
        ? valuation.investmentFocus
        : 'Default',
      name: fund.name,
      portfolioCompanyCount:
        typeof valuation.portfolioCompanyCount === 'number'
          ? valuation.portfolioCompanyCount
          : 0,
      status: 'Live', //TODO - where do we get it from
      year: new Date(fund.inceptionDate).getFullYear(),
    };
  });
}

function getNumericValue(val: string | number | null) {
  if (typeof val === 'string') {
    return parseFloat(val);
  }
  if (typeof val === 'number') {
    return val;
  }
  return 0;
}
function getFundValuation(fundId: string, valuations: FundValuation[]) {
  return valuations.find((v) => v.fundId === fundId) || null;
}

const emptyValuation: AddNull<FundValuation> = {
  currentNav: 0,
  valuationDate: null,
  deletedAt: null,
  createdBy: null,
  companyId: null,
  quarterEnd: null,
  grossIrr: 0,
  netIrr: 0,
  grossMoic: 0,
  netTvpi: 0,
  portfolioCompanyCount: 0,
  createdAt: null,
  updatedAt: null,
  id: null,
  fundId: null,
  // currency: null,
  // aum: 0,
  investmentFocus: null,
};
